<template>
  <el-col :span="24" id="customer" v-loading.body="(receiving || changingPage) && !navigating" element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" id="customer-header">
      <HeaderInfoText :colspan="9" title="CUSTOMERS" :meta="meta" :selection="selection"></HeaderInfoText>
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            @current-change="changePage"
            :current-page="currentPage">
          </el-pagination>
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <el-button plain @click="toggleFilters">
          {{ showFilters ? "Hide filters" : "Show filters" }}
        </el-button>
        <el-button type="primary" icon="plus" @click="createNewModel"></el-button>
      </el-col>
    </el-row>
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        clearAction="clearCustomerState"
        clearMetadataAction="resetCustomerMeta"
        searchAction="fetchCustomersWithQuery"
        :searchForDeps="true"
        :autoApply="false"
        @filterCleared="filtersHaveBeenApplied">
        <template v-for="(f, i) in filters" slot-scope="props" :slot="`${f.type}${i}`">
          <component :key="i" :is="f.type" :properties="f.props" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <el-table
          v-if="meta.count && !changingPage"
          id="customer-table"
          ref="customerTable"
          :data="customer"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight"
          @select="handleSelectionChange"
          @select-all="handleSelectionChange">
          <el-table-column type="selection" width="55" align="center"></el-table-column>
          <el-table-column prop="code" label="Code" align="left" fit>
            <template slot-scope="scope">
              <el-row type="flex">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.code }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="Name" align="left" fit>
            <template slot-scope="scope">
              <el-row type="flex">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.name }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="sales_rep" label="Sales Rep" align="left" fit>
            <template slot-scope="scope">
              {{ getSalesrepById(scope.row.sales_rep).name }}
            </template>
          </el-table-column>
          <el-table-column prop="active" label="Active" align="center" width="120">
            <template slot-scope="scope">
              <el-tag :type="scope.row.active ? 'success' : 'danger'" close-transition>
                {{ scope.row.active ? 'Yes' : 'No' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>
    <CustomerVendorForm :type="customerVendorFormType" :modelId="modelId" @updateModelIdProp="updateCustomerModelId"></CustomerVendorForm>
  </el-col>
</template>

<script>
  /* eslint-disable no-unused-vars */
  import { mapGetters } from 'vuex'
  import { payload as customerActionPayload } from '@/components/RouteComponents/Customers/actionPayload'
  import store from '@/store/store'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import CustomerVendorForm from '@/components/FormComponents/CustomerVendorForm.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { notify } from '@/utils/helpers/Notify'
  import { handleSelectionChange, restoreSelection } from '@/components/RouteComponents/commonFunctions'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      CustomerVendorForm,
      HeaderInfoText
    },
    data () {
      return {
        filters: [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'entity_type',
              clearable: true,
              opts: [
                { label: 'Not a 1099 Vendor', value: 'NOT_1099' },
                { label: 'Corporation', value: 'CORPORATION' },
                { label: 'LLC', value: 'LLC' },
                { label: 'LLC (Classified as a Corp)', value: 'LLC_CORP' },
                { label: 'Legal Firm', value: 'LEGAL_FIRM' },
                { label: 'Partnership', value: 'PARTNERSHIP' },
                { label: 'Sole Proprietor (SSN)', value: 'SOLE_PROPRIETOR_SSN' },
                { label: 'Sole Proprietor (EIN)', value: 'SOLE_PROPRIETOR_EIN' },
                { label: 'Individual', value: 'INDIVIDUAL' },
                { label: 'Other', value: 'OTHER' },
                { label: 'Unknown', value: 'UNKNOWN' }
              ],
              multi: false,
              placeholder: 'Entity type'
            }
          }
        ],
        tableHeight: 1000,
        showFilters: true,
        selection: [],
        currentPage: 1,
        modelId: null,
        changingPage: false
      }
    },
    methods: {
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        handleSelectionChange(this, selection)
      },
      /**
       * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
       * due to a page or tab change
       */
      restoreSelection () {
        restoreSelection(this, 'customerTable')
      },
      /**
       * Handles paginating between production pages
       * @param page  Target page
       */
      async changePage (page) {
        this.changingPage = true
        const { currentFilters } = this
        let payload = currentFilters && Object.keys(currentFilters).length
          ? Object.assign({}, customerActionPayload, { query: { ...currentFilters, ...{ page } } })
          : Object.assign({}, customerActionPayload, { query: { page } })
        try {
          await this.$store.dispatch('clearCustomerState', true)
          const resp = await this.$store.dispatch('fetchWithQuery', payload)
          if (resp && resp.entities) {
            const promises = await this.$store.dispatch('findDependencies', resp.entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.restoreSelection()
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      },
      /**
       * Show or hide filters
       */
      toggleFilters () {
        !this.showFilters ? this.showFilters = true : this.showFilters = false
      },
      /**
       * Resets modelId data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        this.modelId = null
        this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible: true, type: 'customer' })
      },
      /**
       * Handles editing an existing customer
       * @param modelId   The id of the account that is being edited
       */
      editExistingModel (modelId) {
        this.modelId = modelId
        this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible: true, type: 'customer' })
      },
      /**
       * Turns edit mode off
       */
      disableEditMode () {
        this.modelId = null
      },
      /**
       * Updates modelId prop passed to customer create/edit modal
       */
      updateCustomerModelId (id) {
        this.modelId = id
      },
      async filtersHaveBeenApplied () {
        try {
          await this.changePage(1)
          this.currentPage = 1
        } catch (e) {
          console.log(e)
        }
      }
    },
    computed: {
      ...mapGetters({
        meta: 'getCustomerMetadata',
        customer: 'getCustomers',
        receiving: 'getCustomerFetchStatus',
        getCustomerById: 'getCustomerById',
        getSalesrepById: 'getSalesrepById',
        getAddressById: 'getAddressById',
        navigating: 'getNavigationStatus',
        userIsEditingForm: 'getIsUserEditingForm',
        routeLeaveConfirmation: 'getRouteLeaveConfirmation',
        modalVisibilityState: 'getModalVisibilityState',
        currentFilters: 'getFilters'
      }),
      customerVendorFormType () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.customerVendorForm && typeof visible.customerVendorForm === 'object' && visible.customerVendorForm.visible) {
          return visible.customerVendorForm.type
        }
        return 'customer'
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      try {
        await store.dispatch('clearLocalState', { clearMutation: customerActionPayload.clearMutation, clearCache: true })
        const r = await store.dispatch('fetchWithQuery', customerActionPayload)
        if (r && r.entities) {
          const promises = await store.dispatch('findDependencies', r.entities)
          await Promise.all(promises)
        }
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('customer').offsetHeight
          const headerHeight = document.getElementById('customer-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    },
    async beforeRouteLeave (to, from, next) {
      if (this.modalVisibilityState.vendor && this.modalVisibilityState.vendor.visible && !this.routeLeaveConfirmation && this.userIsEditingForm) {
        try {
          await this.$store.dispatch('setDestinationRoute', to)
          await this.$store.dispatch('changeModalVisibility', { entityType: 'changeLossModal', visible: true })
        } catch (e) {
          console.log(e)
        }
      } else {
        try {
          await this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible: false, type: 'customer' })
          await this.$store.dispatch('clearFilters')
        } catch (e) {
          console.log(e)
        }
        next()
      }
    }
  }
</script>

<style scoped>
  #customer-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Customers/Customers.vue